import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Spin,
  Button,
  Tag,
  Row,
  Col,
  Alert,
  Segmented,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  LineChartOutlined,
  DollarOutlined,
  AccountBookOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import LandingLayout from './LandingLayout';
import {
  getFieldsByIndustry,
  buildMetricRows,
  sortIssuerFinancialReportsDesc,
  getFinancialReportPeriodKind,
  financialReportsAppearEqual,
} from '../../constants/financialConstants';
import apiClient from '../../services/apiClient';
import "./FinancialDetails.css";

const { Title, Text } = Typography;

const auditBrief = (columnUsed) => {
  if (!columnUsed || typeof columnUsed !== 'string') return '';
  const lower = columnUsed.toLowerCase();
  if (lower.includes('unaudited')) return ' — Unaudited';
  if (lower.includes('audited')) return ' — Audited';
  if (lower.includes('limited review')) return ' — Ltd. review';
  return '';
};

const formatReportOptionLabel = (r) =>
  `${r.periodType || 'Period'} · ${r.endDate || r.periodEnd || '—'}${auditBrief(r.columnUsed)}`;

const pickerValueForReport = (kind, sortedAnnual, sortedQuarterly, report) => {
  const list = kind === 'annual' ? sortedAnnual : sortedQuarterly;
  const idx = list.findIndex((x) => financialReportsAppearEqual(x, report));
  if (idx < 0) return undefined;
  return `${kind}:${idx}`;
};

const inferKindForReportInLists = (report, sortedA, sortedQ) => {
  const ia = sortedA.findIndex((r) => financialReportsAppearEqual(r, report));
  const iq = sortedQ.findIndex((r) => financialReportsAppearEqual(r, report));
  if (ia >= 0 && iq < 0) return 'annual';
  if (iq >= 0 && ia < 0) return 'quarterly';
  if (ia >= 0 && iq >= 0) return getFinancialReportPeriodKind(report);
  return getFinancialReportPeriodKind(report);
};

const FinancialDetails = ({ skipLayout = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [financialData, setFinancialData] = useState(null);
  const [issuerData, setIssuerData] = useState(null);
  const [annualReports, setAnnualReports] = useState([]);
  const [quarterlyReports, setQuarterlyReports] = useState([]);
  const [periodKind, setPeriodKind] = useState('annual');
  const [listsLoading, setListsLoading] = useState(false);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  const stateDataSignature = location.state?.data
    ? [
        location.state.data.periodType,
        location.state.data.endDate || location.state.data.periodEnd,
        location.state.data.columnUsed,
        location.state.data.id?.statementType,
      ].join('|')
    : '';

  const issuerArraysPresent =
    Array.isArray(location.state?.annualReports) &&
    Array.isArray(location.state?.quarterlyReports);

  const issuerIdentity = useMemo(
    () =>
      location.state?.issuerName ||
      location.state?.issuer?.issuer ||
      location.state?.data?.id?.companyName ||
      location.state?.data?.issuerName ||
      '',
    [
      location.state?.issuerName,
      location.state?.issuer?.issuer,
      location.state?.data?.id?.companyName,
      location.state?.data?.issuerName,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    const st = location.state;
    const data = st?.data;

    if (!data) {
      setBootstrapError('No financial data provided');
      setFinancialData(null);
      setListsLoaded(true);
      return;
    }

    setBootstrapError(null);
    setFinancialData(data);

    if (st?.issuer) {
      setIssuerData(st.issuer);
    } else if (st?.data?.issuerName || st?.data?.id?.companyName) {
      setIssuerData({
        issuer: st?.data?.issuerName || st?.data?.id?.companyName,
        basicIndustry: st?.basicIndustry,
      });
    } else {
      setIssuerData({
        issuer: data.issuerName || data.id?.companyName,
        basicIndustry: st?.basicIndustry,
      });
    }

    const loadLists = async () => {
      if (issuerArraysPresent) {
        const a = sortIssuerFinancialReportsDesc(st.annualReports);
        const q = sortIssuerFinancialReportsDesc(st.quarterlyReports);
        if (!cancelled) {
          setAnnualReports(a);
          setQuarterlyReports(q);
          setListsLoading(false);
          setListsLoaded(true);
        }
        return;
      }

      const name =
        issuerIdentity ||
        data?.id?.companyName ||
        data?.issuerName;

      if (!name) {
        if (!cancelled) {
          setAnnualReports([]);
          setQuarterlyReports([]);
          setListsLoading(false);
          setListsLoaded(true);
        }
        return;
      }

      setListsLoading(true);
      try {
        const url = `${baseUrl}/issuer-financial-data/issuer?issuerName=${encodeURIComponent(name)}`;
        const response = await apiClient.get(url, { validateStatus: () => true });

        if (cancelled) return;

        if (response.status !== 200 || response.data?.statusCode !== 200 || !response.data?.data) {
          setAnnualReports([]);
          setQuarterlyReports([]);
        } else {
          const payload = response.data.data;
          const a = sortIssuerFinancialReportsDesc(payload.annualReports || []);
          const q = sortIssuerFinancialReportsDesc(payload.quarterlyReports || []);
          setAnnualReports(a);
          setQuarterlyReports(q);
        }
      } catch (_) {
        if (!cancelled) {
          setAnnualReports([]);
          setQuarterlyReports([]);
        }
      } finally {
        if (!cancelled) {
          setListsLoading(false);
          setListsLoaded(true);
        }
      }
    };

    setListsLoaded(false);
    loadLists();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- arrays presence gates cached vs API load
  }, [location.pathname, issuerIdentity, issuerArraysPresent]);

  /** Lists loaded or reporting period changed via router — sync filing + Annual/Quarterly tab */
  useEffect(() => {
    if (!listsLoaded) return;
    const row = location.state?.data;
    if (!row) return;
    setFinancialData((prev) => (prev && financialReportsAppearEqual(prev, row) ? prev : row));
    setPeriodKind(inferKindForReportInLists(row, annualReports, quarterlyReports));
  }, [listsLoaded, annualReports, quarterlyReports, stateDataSignature]);

  const persistReportToRouter = useCallback(
    (report) => {
      navigate('.', {
        replace: true,
        state: {
          ...location.state,
          data: report,
        },
      });
    },
    [navigate, location.state]
  );

  const reportCountTotal = annualReports.length + quarterlyReports.length;
  const showToolbar = listsLoaded && reportCountTotal > 1;

  const activeList = periodKind === 'annual' ? annualReports : quarterlyReports;

  const selectOptions = activeList.map((r, idx) => ({
    value: `${periodKind}:${idx}`,
    label: formatReportOptionLabel(r),
  }));

  const selectValue =
    financialData &&
    pickerValueForReport(periodKind, annualReports, quarterlyReports, financialData);

  const onPeriodKindSegment = (kind) => {
    if (!financialData) return;
    const nextList = kind === 'annual' ? annualReports : quarterlyReports;
    if (nextList.length === 0) return;

    setPeriodKind(kind);

    const curEnd = financialData.endDate || financialData.periodEnd;
    const stmt = financialData.id?.statementType || '';

    const sameCalendarStmt =
      nextList.find(
        (r) =>
          (r.endDate || r.periodEnd) === curEnd &&
          (r.id?.statementType || '') === stmt
      ) || nextList[0];

    setFinancialData(sameCalendarStmt);
    persistReportToRouter(sameCalendarStmt);
  };

  const onSelectPeriod = (val) => {
    const raw = String(val);
    const colon = raw.indexOf(':');
    const kind = colon >= 0 ? raw.slice(0, colon) : 'annual';
    const idx = parseInt(raw.slice(colon + 1), 10);
    const list =
      kind === 'quarterly' ? quarterlyReports : annualReports;
    const report = Number.isFinite(idx) && idx >= 0 && idx < list.length ? list[idx] : null;
    if (!report) return;
    setPeriodKind(kind === 'quarterly' ? 'quarterly' : 'annual');
    setFinancialData(report);
    persistReportToRouter(report);
  };

  if (bootstrapError || !location.state?.data) {
    return skipLayout ? (
      <MissingStateAlert navigate={navigate} message={bootstrapError || 'No financial data provided'} />
    ) : (
      <LandingLayout>
        <MissingStateAlert navigate={navigate} message={bootstrapError || 'No financial data provided'} />
      </LandingLayout>
    );
  }

  if (!financialData) {
    return skipLayout ? (
      <FinancialLoadingPane />
    ) : (
      <LandingLayout>
        <FinancialLoadingPane />
      </LandingLayout>
    );
  }

  const basicIndustry = issuerData?.basicIndustry || location.state?.basicIndustry || '';
  const periodSlug =
    financialData.periodType &&
    String(financialData.periodType).toLowerCase().includes('quarter')
      ? 'quarterly'
      : 'annual';

  const fields = getFieldsByIndustry(basicIndustry, periodSlug);

  const incomeData = buildMetricRows(financialData, fields.income);
  const assetData = buildMetricRows(financialData, fields.assets);
  const liabilityData = buildMetricRows(financialData, fields.liabilities);
  const cashFlowData = buildMetricRows(financialData, fields.cashFlow);
  const keyMetricData = buildMetricRows(financialData, fields.keyMetrics);

  const companyName = financialData.id?.companyName || financialData.companyName || financialData.issuerName || 'N/A';
  const endDate = financialData.endDate || financialData.periodEnd || 'N/A';
  const reportingQuarter = financialData.id?.reportingQuarter || financialData.reportingQuarter || 'N/A';
  const dateOfStartOfFinancialYear =
    financialData.id?.dateOfStartOfFinancialYear || financialData.dateOfStartOfFinancialYear || 'N/A';
  const periodTypeDisplay = financialData.periodType || periodSlug || 'N/A';

  const statementTypeMeta = financialData.id?.statementType;

  const statementSections = [
    {
      id: 'income',
      title: 'Income Statement',
      icon: <LineChartOutlined className="financial-detail-card-icon" />,
      data: incomeData,
    },
    {
      id: 'cashflow',
      title: 'Cash Flow Statement',
      icon: <DollarOutlined className="financial-detail-card-icon" />,
      data: cashFlowData,
    },
    {
      id: 'assets',
      title: 'Assets',
      icon: <AccountBookOutlined className="financial-detail-card-icon" />,
      data: assetData,
    },
    {
      id: 'liabilities',
      title: 'Liabilities',
      icon: <AccountBookOutlined className="financial-detail-card-icon" />,
      data: liabilityData,
    },
  ].filter((section) => section.data.length > 0);

  const useTwoColumnKeyMetrics = keyMetricData.length > 3;

  const renderMetricList = (data, showHeader = true) => (
    <div className="financial-detail-metric-list">
      {showHeader && (
        <div className="financial-detail-metric-list-header">
          <span>Metric</span>
          <span>Value</span>
        </div>
      )}
      {data.map((row) => (
        <div className="financial-detail-metric-list-row" key={row.key || row.item}>
          <span className="financial-detail-metric-list-label">{row.item}</span>
          <span className="financial-detail-metric-list-value">{row.value}</span>
        </div>
      ))}
    </div>
  );

  const renderMetricGrid = (data, useTwoColumns) => {
    const mid = Math.ceil(data.length / 2);
    const leftRows = data.slice(0, mid);
    const rightRows = data.slice(mid);

    if (!useTwoColumns || rightRows.length === 0) {
      return renderMetricList(data);
    }

    return (
      <div className="financial-detail-key-metrics-grid">
        <div className="financial-detail-key-metrics-column">
          {renderMetricList(leftRows)}
        </div>
        <div className="financial-detail-key-metrics-column">
          {renderMetricList(rightRows, false)}
        </div>
      </div>
    );
  };

  const renderSectionCard = (section) => {
    const isSingleFullWidthSection = statementSections.length === 1;
    const useTwoColumnSection = isSingleFullWidthSection && section.data.length > 3;

    return (
      <Card
        key={section.id}
        className="financial-detail-section-card"
        title={
          <span className="financial-detail-card-title-text">
            {section.icon}
            <span>{section.title}</span>
          </span>
        }
      >
        {renderMetricGrid(section.data, useTwoColumnSection)}
      </Card>
    );
  };

  const content = (
    <div className={`financial-details-container financial-details-page ${isDark ? 'dark' : ''}`}>
      <header className="financial-details-header">
        <div className="financial-details-header-inner">
          <nav className="financial-details-breadcrumb" aria-label="Breadcrumb">
            <button
              type="button"
              className="financial-details-breadcrumb-link"
              onClick={() => navigate(-1)}
            >
              Issuer
            </button>
            <span className="financial-details-breadcrumb-sep" aria-hidden>›</span>
            <span className="financial-details-breadcrumb-current">Financial Details</span>
          </nav>
          <div className="financial-details-title-row">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="financial-details-btn-back"
            >
              Back
            </Button>
            <div className="financial-details-title-block">
              <Title level={2} className="financial-details-title">
                <BankOutlined /> {companyName}
              </Title>
              <div className="financial-details-subtitle">
                <Tag color="blue">{periodTypeDisplay.toUpperCase()}</Tag>
                {statementTypeMeta && <Tag>{statementTypeMeta}</Tag>}
                {financialData.columnUsed && (
                  <Tag color="default">{financialData.columnUsed}</Tag>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="financial-details-metrics">
        <div className="financial-details-metrics-inner">
          <div className="financial-details-metric">
            <span className="financial-details-metric-label">Period type</span>
            <span className="financial-details-metric-value accent">{periodTypeDisplay}</span>
          </div>
          <div className="financial-details-metric">
            <span className="financial-details-metric-label">End date</span>
            <span className="financial-details-metric-value">{endDate}</span>
          </div>
          <div className="financial-details-metric">
            <span className="financial-details-metric-label">Reporting quarter</span>
            <span className="financial-details-metric-value">{reportingQuarter}</span>
          </div>
          <div className="financial-details-metric">
            <span className="financial-details-metric-label">FY start</span>
            <span className="financial-details-metric-value">{dateOfStartOfFinancialYear}</span>
          </div>
          {statementTypeMeta && (
            <div className="financial-details-metric">
              <span className="financial-details-metric-label">Statement</span>
              <span className="financial-details-metric-value">{statementTypeMeta}</span>
            </div>
          )}
        </div>
      </div>

      <div className="financial-details-content">
        {showToolbar && (
          <Card className="financial-detail-section-card financial-detail-period-toolbar">
            <Text type="secondary" className="financial-detail-toolbar-hint">
              Switch filing period here. Labels include audit status when available.
            </Text>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={10} lg={8}>
                <span className="financial-detail-toolbar-label">Filing type</span>
                <Segmented
                  block
                  value={periodKind}
                  onChange={onPeriodKindSegment}
                  options={[
                    {
                      label: `Annual (${annualReports.length})`,
                      value: 'annual',
                      disabled: annualReports.length === 0,
                    },
                    {
                      label: `Quarterly (${quarterlyReports.length})`,
                      value: 'quarterly',
                      disabled: quarterlyReports.length === 0,
                    },
                  ]}
                />
              </Col>
              <Col xs={24} md={14} lg={16}>
                <span className="financial-detail-toolbar-label">Period</span>
                <Select
                  style={{ width: '100%' }}
                  value={selectValue}
                  options={selectOptions}
                  loading={listsLoading}
                  placeholder="Choose period"
                  onChange={onSelectPeriod}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
            </Row>
          </Card>
        )}

        {statementSections.length === 0 && keyMetricData.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No financial statement data"
            description="This filing does not include statement sections for the selected period."
            className="financial-details-empty-alert"
          />
        ) : (
          <>
            {statementSections.length > 0 && (
              <div
                className={`financial-details-sections-grid${
                  statementSections.length === 1 ? ' financial-details-sections-grid--single' : ''
                }`}
              >
                {statementSections.map(renderSectionCard)}
              </div>
            )}

            {keyMetricData.length > 0 && (
              <Card
                className={`financial-detail-section-card financial-detail-key-metrics-card${
                  statementSections.length > 0 ? ' financial-detail-key-metrics-card--spaced' : ''
                }`}
                title={
                  <span className="financial-detail-card-title-text">
                    <BarChartOutlined className="financial-detail-card-icon" />
                    <span>Key Metrics &amp; Ratios</span>
                  </span>
                }
              >
                {renderMetricGrid(keyMetricData, useTwoColumnKeyMetrics)}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );

  return skipLayout ? content : <LandingLayout>{content}</LandingLayout>;
};

function FinancialLoadingPane() {
  return (
    <div className="financial-details-container financial-details-page">
      <div className="financial-details-loading">
        <Spin size="large" />
        <Text>Loading financial details...</Text>
      </div>
    </div>
  );
}

function MissingStateAlert({ navigate, message }) {
  return (
    <div className="financial-details-container financial-details-page">
      <div className="financial-details-error">
        <Alert
          message="Error"
          description={message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </div>
    </div>
  );
}

export default FinancialDetails;
